/**
 * Utility functions for handling IPFS URLs and gateways
 */

// List of IPFS gateways to try as fallbacks
const IPFS_GATEWAYS = [
  'https://ipfs.io/ipfs/',
  'https://dweb.link/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://ipfs.infura.io/ipfs/',
  'https://ipfs.filebase.io/ipfs/', // Fallback for existing Filebase uploads
  'https://4everland.io/ipfs/',
  'https://w3s.link/ipfs/',
  'https://cf-ipfs.com/ipfs/',
];

/**
 * Convert an IPFS URL to use a specific gateway
 */
export function convertIpfsUrl(ipfsUrl: string, gatewayIndex: number = 0): string {
  if (!ipfsUrl) return '';
  
  // If it's already a full URL, extract the CID
  let cid = '';
  let filename = '';
  
  if (ipfsUrl.startsWith('ipfs://')) {
    // Handle ipfs:// format
    const path = ipfsUrl.replace('ipfs://', '');
    const parts = path.split('/');
    cid = parts[0];
    filename = parts.slice(1).join('/');
  } else if (ipfsUrl.includes('ipfs.w3s.link')) {
    // Handle Web3.Storage format: https://{cid}.ipfs.w3s.link/{filename}
    const match = ipfsUrl.match(/https:\/\/([^.]+)\.ipfs\.w3s\.link\/(.+)/);
    if (match) {
      cid = match[1];
      filename = match[2];
    }
  } else if (ipfsUrl.includes('ipfs.filebase.io')) {
    // Handle Filebase format: https://ipfs.filebase.io/ipfs/{cid}/{filename}
    const match = ipfsUrl.match(/https:\/\/ipfs\.filebase\.io\/ipfs\/([^\/]+)\/(.+)/);
    if (match) {
      cid = match[1];
      filename = match[2];
    }
  } else if (ipfsUrl.includes('/ipfs/')) {
    // Handle generic IPFS format: https://gateway.com/ipfs/{cid}/{filename}
    const match = ipfsUrl.match(/https:\/\/[^\/]+\/ipfs\/([^\/]+)\/(.+)/);
    if (match) {
      cid = match[1];
      filename = match[2];
    }
  }
  
  if (!cid) {
    console.warn('Could not extract CID from IPFS URL:', ipfsUrl);
    return ipfsUrl;
  }
  
  const gateway = IPFS_GATEWAYS[gatewayIndex] || IPFS_GATEWAYS[0];
  return filename ? `${gateway}${cid}/${filename}` : `${gateway}${cid}`;
}

/**
 * Get the next gateway URL for fallback
 */
export function getNextGatewayUrl(originalUrl: string, currentGatewayIndex: number): string | null {
  if (currentGatewayIndex >= IPFS_GATEWAYS.length - 1) {
    return null; // No more gateways to try
  }
  return convertIpfsUrl(originalUrl, currentGatewayIndex + 1);
}

/**
 * Check if a URL is an IPFS URL
 */
export function isIpfsUrl(url: string): boolean {
  return url.startsWith('ipfs://') || 
         url.includes('ipfs.w3s.link') || 
         url.includes('ipfs.filebase.io') || 
         url.includes('/ipfs/');
}

/**
 * Extract CID from an IPFS URL
 */
export function extractCidFromUrl(url: string): string | null {
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', '').split('/')[0];
  }
  
  const patterns = [
    /https:\/\/([^.]+)\.ipfs\.w3s\.link\//, // Web3.Storage
    /https:\/\/[^\/]+\/ipfs\/([^\/]+)\//, // Generic
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }
  
  return null;
}

// Function to process image data and return appropriate URL
export function processImageData(imageData: string): string {
  if (!imageData) return '';
  
  // If it's already a URL
  if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
    return imageData;
  }
  
  // If it's base64 data
  if (imageData.startsWith('data:image/')) {
    return imageData;
  }
  
  // Handle 'cid|filename' format
  if (imageData.includes('|')) {
    const [cid, filename] = imageData.split('|');
    if (cid && filename) {
      return `https://ipfs.io/ipfs/${cid}/${filename}`;
    }
  }
  
  // Handle 'cid/filename' format (directory CID with filename)
  if (imageData.includes('/') && !imageData.startsWith('http')) {
    const [cid, filename] = imageData.split('/');
    if (cid && filename) {
      return `https://ipfs.io/ipfs/${cid}/${filename}`;
    }
  }
  
  // If it's an actual IPFS CID (46+ character alphanumeric string)
  if (imageData && /^[a-zA-Z0-9]{46,}$/.test(imageData)) {
    // This is likely a single file CID
    return `https://ipfs.io/ipfs/${imageData}`;
  }
  
  // If it's IPFS URL
  if (imageData.startsWith('ipfs://')) {
    return `https://${imageData.replace('ipfs://', '')}.ipfs.w3s.link`;
  }
  
  // If it's already a w3s.link URL
  if (imageData.includes('ipfs.w3s.link')) {
    return imageData;
  }
  
  // If it's the old w3s.link format and convert to new format
  if (imageData.includes('w3s.link/ipfs/')) {
    const parts = imageData.split('/ipfs/');
    if (parts.length === 2) {
      const cidAndPath = parts[1];
      const cid = cidAndPath.split('/')[0];
      const path = cidAndPath.split('/').slice(1).join('/');
      return `https://${cid}.ipfs.w3s.link/${path}`;
    }
  }
  
  // Assume it's base64 and convert to data URL
  let mimeType = 'image/png'; // default
  if (imageData.startsWith('/9j/')) {
    mimeType = 'image/jpeg';
  } else if (imageData.startsWith('iVBORw0KGgo')) {
    mimeType = 'image/png';
  } else if (imageData.startsWith('R0lGODlh')) {
    mimeType = 'image/gif';
  } else if (imageData.startsWith('UklGR')) {
    mimeType = 'image/webp';
  }
  
  return `data:${mimeType};base64,${imageData}`;
}

// Function to check if a string is an IPFS CID or directory ID
export function isIpfsCid(str: string): boolean {
  // Check for pipe separator format: cid|filename
  if (str.includes('|')) {
    const [cid] = str.split('|');
    // Check if the first part looks like an IPFS CID
    if (/^[a-zA-Z0-9]{46,}$/.test(cid)) {
      return true;
    }
  }
  
  // Check for old format: cid/filename (with slash separator)
  // We only care about the CID part
  if (str.includes('/') && !str.startsWith('http')) {
    const parts = str.split('/');
    if (parts.length === 2) {
      const [cid] = parts;
      // Check if the first part looks like an IPFS CID
      if (/^[a-zA-Z0-9]{46,}$/.test(cid)) {
        return true;
      }
    }
  }
  
  // Check if it's an actual IPFS CID (46+ character alphanumeric string)
  if (str && /^[a-zA-Z0-9]{46,}$/.test(str)) {
    return true;
  }
  
  return false;
}

// Function to process UI page data and return appropriate URL
export function processUIPageData(pageData: string): string {
  if (!pageData) return '';
  
  // Check if it's already a URL
  if (pageData.startsWith('http://') || pageData.startsWith('https://')) {
    return pageData;
  }
  
  // Check if it's an actual IPFS CID (46+ character alphanumeric string)
  if (pageData && /^[a-zA-Z0-9]{46,}$/.test(pageData)) {
    // This is likely an actual IPFS CID from local IPFS node
    // For directory uploads, append index.html
    return `https://ipfs.io/ipfs/${pageData}/index.html`;
  }
  
  // Check if it's IPFS URL
  if (pageData.startsWith('ipfs://')) {
    return `https://${pageData.replace('ipfs://', '')}.ipfs.w3s.link/index.html`;
  }
  
  // Check if it's already a w3s.link URL
  if (pageData.includes('ipfs.w3s.link')) {
    // Append index.html if not already present
    if (!pageData.endsWith('/index.html')) {
      return pageData.endsWith('/') ? `${pageData}index.html` : `${pageData}/index.html`;
    }
    return pageData;
  }
  
  // If it's not a recognized format, return as-is
  return pageData;
} 

/**
 * Test an IPFS URL with multiple gateways and return the first working one
 */
export async function testIpfsUrl(cid: string, path: string = 'index.html', timeout: number = 5000): Promise<string | null> {
  const gateways = [
    'https://ipfs.io/ipfs/',
    'https://dweb.link/ipfs/',
    'https://cloudflare-ipfs.com/ipfs/',
    'https://gateway.pinata.cloud/ipfs/',
    'https://ipfs.infura.io/ipfs/',
    'https://4everland.io/ipfs/',
    'https://w3s.link/ipfs/',
    'https://cf-ipfs.com/ipfs/',
  ];

  console.log(`Testing IPFS URL with ${gateways.length} gateways for CID: ${cid}`);

  for (let i = 0; i < gateways.length; i++) {
    const gateway = gateways[i];
    const url = `${gateway}${cid}/${path}`;
    
    console.log(`Trying gateway ${i + 1}/${gateways.length}: ${gateway}`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, {
        signal: controller.signal,
        method: 'HEAD',
        mode: 'cors'
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        console.log(`IPFS URL test successful with gateway ${i + 1}:`, gateway);
        return url;
      } else {
        console.warn(`Gateway ${gateway} returned status: ${response.status}`);
      }
    } catch (error) {
      console.warn(`Gateway ${gateway} failed:`, error);
      // Continue to next gateway
    }
  }
  
  console.error('All IPFS gateways failed for URL test');
  return null;
} 